import React, { useState, useEffect } from 'react';
import { 
  CalendarIcon, 
  FilterIcon, 
  SearchIcon,
  ArrowUpIcon, 
  ArrowDownIcon, 
  CheckCircleIcon, 
  XCircleIcon, 
  AlertCircleIcon,
  PlusIcon,
  PencilIcon,
  TrashIcon
} from 'lucide-react';
import { 
  customerServices,
  subscriptionServices, 
  subscriptionPlanServices, 
  Subscription, 
  Customer, 
  SubscriptionPlan 
} from '../lib/supabase';
// Import the fixed subscription services
import { fixedSubscriptionServices } from '../lib/fixed-subscription-service';

const Subscriptions = () => {
  // State variables
  const [dateRange, setDateRange] = useState('last30Days');
  const [searchQuery, setSearchQuery] = useState('');
  const [subscriptions, setSubscriptions] = useState<Subscription[]>([]);
  const [customers, setCustomers] = useState<Customer[]>([]);
  const [plans, setPlans] = useState<SubscriptionPlan[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showModal, setShowModal] = useState(false);
  const [currentSubscription, setCurrentSubscription] = useState<Partial<Subscription> | null>(null);
  
  // Subscription metrics - will be calculated from actual data
  const [subscriptionMetrics, setSubscriptionMetrics] = useState([
    {
      title: 'Active Subscriptions',
      value: '0',
      change: '+0',
      isPositive: true
    }, 
    {
      title: 'Trial Conversions',
      value: '0%',
      change: '+0%',
      isPositive: true
    }, 
    {
      title: 'Churn Rate',
      value: '0%',
      change: '0%',
      isPositive: true
    }, 
    {
      title: 'MRR',
      value: '$0',
      change: '+0%',
      isPositive: true
    }
  ]);

  // Fetch data on component mount
  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        
        // Fetch subscriptions - using original service for fetching
        const { data: subscriptionsData, error: subscriptionsError } = await subscriptionServices.getAll();
        if (subscriptionsError) throw subscriptionsError;
        
        // Fetch customers for reference
        const { data: customersData, error: customersError } = await customerServices.getAll();
        if (customersError) throw customersError;
        
        // Process customer data - ensure IDs are strings
        let processedCustomers = customersData;
        if (customersData) {
          processedCustomers = customersData.map(customer => ({
            ...customer,
            id: String(customer.id)  // Ensure ID is a string
          }));
          console.log('Processed customers with string IDs:', processedCustomers);
        }
        
        // Fetch plans for reference
        const { data: plansData, error: plansError } = await subscriptionPlanServices.getAll();
        if (plansError) throw plansError;
        
        // Process the data - ensure IDs are strings
        let processedPlans = plansData;
        if (plansData) {
          processedPlans = plansData.map(plan => ({
            ...plan,
            id: String(plan.id)  // Ensure ID is a string
          }));
          console.log('Processed plans with string IDs:', processedPlans);
        }
        
        // Set the data in state
        if (subscriptionsData) setSubscriptions(subscriptionsData);
        if (processedCustomers) setCustomers(processedCustomers);
        if (processedPlans) setPlans(processedPlans);
        
        // Calculate metrics
        calculateMetrics(subscriptionsData || []);
        
      } catch (error: any) {
        setError(error.message || 'Failed to fetch data');
        console.error('Error fetching data:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  // Calculate subscription metrics
  const calculateMetrics = (subscriptions: Subscription[]) => {
    try {
      // Count active subscriptions
      const activeCount = subscriptions.filter(sub => sub.status.toLowerCase() === 'active').length;
      
      // Calculate trial conversion rate (simplified example)
      const trialCount = subscriptions.filter(sub => sub.status.toLowerCase() === 'trialing').length;
      const convertedCount = 0; // This would need more complex logic based on historical data
      const conversionRate = trialCount > 0 ? (convertedCount / trialCount * 100).toFixed(1) : '0.0';
      
      // Calculate churn rate (simplified example)
      const canceledCount = subscriptions.filter(sub => sub.status.toLowerCase() === 'canceled').length;
      const churnRate = subscriptions.length > 0 ? (canceledCount / subscriptions.length * 100).toFixed(1) : '0.0';
      
      // Calculate MRR (Monthly Recurring Revenue)
      let mrr = 0;
      subscriptions.forEach(sub => {
        if (sub.status.toLowerCase() === 'active') {
          // Find the plan for this subscription
          const plan = plans.find(p => p.id === sub.plan_id);
          if (plan) {
            // If annual plan, divide by 12 to get monthly equivalent
            const monthlyValue = plan.billing_period === 'annual' 
              ? plan.price / 12
              : plan.price;
            mrr += monthlyValue;
          }
        }
      });
      
      // Update metrics
      setSubscriptionMetrics([
        {
          title: 'Active Subscriptions',
          value: activeCount.toString(),
          change: '+0', // This would need historical data to calculate change
          isPositive: true
        },
        {
          title: 'Trial Conversions',
          value: `${conversionRate}%`,
          change: '+0%', // This would need historical data to calculate change
          isPositive: true
        },
        {
          title: 'Churn Rate',
          value: `${churnRate}%`,
          change: '0%', // This would need historical data to calculate change
          isPositive: churnRate === '0.0'
        },
        {
          title: 'MRR',
          value: `$${mrr.toFixed(2)}`,
          change: '+0%', // This would need historical data to calculate change
          isPositive: true
        }
      ]);
      
    } catch (err) {
      console.error('Error calculating metrics:', err);
    }
  };

  // Get customer name by ID
  const getCustomerName = (customerId: string) => {
    const customer = customers.find(c => c.id === customerId);
    return customer ? customer.name : 'Unknown Customer';
  };

  // Get plan name and price by ID
  const getPlanInfo = (planId: string) => {
    const plan = plans.find(p => p.id === planId);
    return {
      name: plan ? plan.name : 'Unknown Plan',
      price: plan ? plan.price : 0,
      billing_period: plan ? plan.billing_period : 'monthly'
    };
  };

  // Format date for display
  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { 
      month: 'short', 
      day: 'numeric', 
      year: 'numeric' 
    });
  };

  // Get status icon based on status
  const getStatusIcon = (status: string) => {
    switch (status.toLowerCase()) {
      case 'active':
        return <CheckCircleIcon size={16} className="text-green-500" />;
      case 'trialing':
        return <AlertCircleIcon size={16} className="text-blue-500" />;
      case 'canceled':
        return <XCircleIcon size={16} className="text-red-500" />;
      default:
        return null;
    }
  };
  // Filter subscriptions by search query
  const filteredSubscriptions = subscriptions.filter(sub => {
    const customerName = getCustomerName(sub.customer_id).toLowerCase();
    const planName = getPlanInfo(sub.plan_id).name.toLowerCase();
    const searchLower = searchQuery.toLowerCase();
    
    return customerName.includes(searchLower) || 
           planName.includes(searchLower) || 
           sub.status.toLowerCase().includes(searchLower);
  });

  // Open modal to add/edit subscription
  const handleOpenModal = (subscription: Subscription | null = null) => {
    // If editing, use existing subscription data
    if (subscription) {
      // Make a clean copy to avoid reference issues
      setCurrentSubscription({...subscription});
    } else {
      // If creating new, initialize with defaults and empty string IDs to avoid numeric default issues
      setCurrentSubscription({
        customer_id: '', // Start with empty string to ensure proper UUID validation
        plan_id: '', // Start with empty string to ensure proper UUID validation
        status: 'active',
        start_date: new Date().toISOString().split('T')[0],
        end_date: undefined,
        trial_end_date: undefined,
        cancel_at_period_end: false
      });
      
      // Clear any previous error messages
      setError(null);
    }
    
    setShowModal(true);
  };
  
  // Close modal and reset form
  const handleCloseModal = () => {
    setShowModal(false);
    setCurrentSubscription(null);
  };
  
  // Helper function to validate UUID format
  const isValidUUID = (str: string): boolean => {
    const uuidRegex = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
    return uuidRegex.test(str);
  };
  
  // Handle subscription form submit
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    // Enhanced validation for required fields
    if (!currentSubscription) {
      setError('Subscription data is missing');
      return;
    }
    
    // Validate customer_id
    if (!currentSubscription.customer_id) {
      setError('Please select a customer');
      return;
    }
    
    // Ensure customer_id is a string and validate UUID format
    const customerId = String(currentSubscription.customer_id);
    if (!isValidUUID(customerId)) {
      setError(`Selected customer ID (${customerId}) is not a valid UUID`);
      return;
    }
    
    // Validate plan_id
    if (!currentSubscription.plan_id) {
      setError('Please select a subscription plan');
      return; 
    }
    
    // Ensure plan_id is a string and validate UUID format 
    const planId = String(currentSubscription.plan_id);
    if (!isValidUUID(planId)) {
      setError(`Selected plan ID (${planId}) is not a valid UUID`);
      return;
    }
    
    // Validate start_date
    if (!currentSubscription.start_date) {
      setError('Start date is required');
      return;
    }
    
    try {
      let result: { data: Subscription[] | null; error: Error | null };
      
      if (currentSubscription.id) {
        // Update existing subscription using fixed service
        result = await fixedSubscriptionServices.update(currentSubscription.id, currentSubscription);
        
        // Update local state
        if (result.data) {
          setSubscriptions(prevSubs => 
            prevSubs.map(sub => 
              sub.id === currentSubscription.id 
                ? { ...sub, ...currentSubscription } as Subscription
                : sub
            )
          );
        }
      } else {
        // Log data being sent to create
        console.log('Creating subscription with data:', {
          customer_id: currentSubscription.customer_id,
          plan_id: currentSubscription.plan_id,
          status: currentSubscription.status,
          start_date: currentSubscription.start_date
        });
        
        // Create new subscription using fixed service
        result = await fixedSubscriptionServices.create(currentSubscription as Subscription);
        
        // Update local state
        if (result.data) {
          setSubscriptions(prevSubs => [...prevSubs, result.data![0]]);
        }
      }
      
      if (result.error) {
        console.error('Error from subscription service:', result.error);
        throw result.error;
      }
      
      // Recalculate metrics
      calculateMetrics([...subscriptions, ...(result.data || [])]);
      
      // Success! Close the modal
      handleCloseModal();
    } catch (error: any) {
      // Format the error message for better clarity
      const errorMsg = error.message || 'Failed to save subscription';
      setError(`Error: ${errorMsg}`);
      console.error('Error saving subscription:', error);
    }
  };
  
  // Handle subscription deletion
  const handleDelete = async (id: string) => {
    if (window.confirm('Are you sure you want to delete this subscription?')) {
      try {
        const { error } = await fixedSubscriptionServices.delete(id);
        
        if (error) {
          throw error;
        }
        
        // Update local state
        const updatedSubscriptions = subscriptions.filter(sub => sub.id !== id);
        setSubscriptions(updatedSubscriptions);
        
        // Recalculate metrics
        calculateMetrics(updatedSubscriptions);
      } catch (error: any) {
        setError(error.message || 'Failed to delete subscription');
        console.error('Error deleting subscription:', error);
      }
    }
  };

  // Render loading state
  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <h1 className="text-2xl font-bold text-gray-800">Subscriptions</h1>
        <div className="flex flex-wrap gap-2">
          <div className="relative">
            <input
              type="text"
              placeholder="Search subscriptions..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-10 pr-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500 text-sm w-60"
            />
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
              <SearchIcon size={16} className="text-gray-500" />
            </div>
          </div>
          
          <div className="relative">
            <select 
              value={dateRange} 
              onChange={e => setDateRange(e.target.value)} 
              className="appearance-none pl-10 pr-10 py-2 border border-gray-300 bg-white rounded-md focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500 text-sm"
            >
              <option value="last7Days">Last 7 days</option>
              <option value="last30Days">Last 30 days</option>
              <option value="last90Days">Last 90 days</option>
              <option value="lastYear">Last year</option>
              <option value="custom">Custom range</option>
            </select>
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
              <CalendarIcon size={16} className="text-gray-500" />
            </div>
          </div>
          
          <button className="flex items-center px-4 py-2 border border-gray-300 bg-white rounded-md hover:bg-gray-50 focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500 text-sm">
            <FilterIcon size={16} className="mr-2 text-gray-500" />
            Filters
          </button>
          
          <button 
            onClick={() => handleOpenModal()}
            className="flex items-center px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 text-sm font-medium"
          >
            <PlusIcon size={16} className="mr-2" />
            New Subscription
          </button>
        </div>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-md flex items-start">
          <div className="flex-shrink-0 mt-0.5">
            <XCircleIcon size={16} />
          </div>
          <div className="ml-3">
            <p className="text-sm font-medium">{error}</p>
          </div>
          <button 
            className="ml-auto flex-shrink-0 text-red-500 hover:text-red-600"
            onClick={() => setError(null)}
          >
            <XCircleIcon size={16} />
          </button>
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        {subscriptionMetrics.map((metric, index) => (
          <div key={index} className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
            <div className="flex items-center justify-between">
              <h3 className="text-sm font-medium text-gray-500">
                {metric.title}
              </h3>
              <span className={`flex items-center text-sm font-medium ${metric.isPositive ? 'text-green-600' : 'text-red-600'}`}>
                {metric.isPositive ? <ArrowUpIcon size={16} className="mr-1" /> : <ArrowDownIcon size={16} className="mr-1" />}
                {metric.change}
              </span>
            </div>
            <p className="mt-2 text-3xl font-bold text-gray-900">
              {metric.value}
            </p>
          </div>
        ))}
      </div>
      
      <div className="bg-white rounded-lg shadow-sm border border-gray-200">
        <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
          <h2 className="text-lg font-semibold text-gray-800">
            Subscriptions
          </h2>
        </div>
        
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead>
              <tr className="bg-gray-50">
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Customer
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Plan
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Status
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Start Date
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  End Date
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Amount
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {filteredSubscriptions.length === 0 ? (
                <tr>
                  <td colSpan={7} className="px-6 py-4 text-center text-sm text-gray-500">
                    No subscriptions found
                  </td>
                </tr>
              ) : (
                filteredSubscriptions.map((subscription) => {
                  const customerName = getCustomerName(subscription.customer_id);
                  const planInfo = getPlanInfo(subscription.plan_id);
                  
                  return (
                    <tr key={subscription.id} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                        {customerName}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {planInfo.name}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center">
                          {getStatusIcon(subscription.status)}
                          <span className="ml-2 text-sm text-gray-900">
                            {subscription.status.charAt(0).toUpperCase() + subscription.status.slice(1)}
                          </span>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {formatDate(subscription.start_date)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {subscription.end_date ? formatDate(subscription.end_date) : 'N/A'}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        ${planInfo.price.toFixed(2)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                        <div className="flex items-center justify-end space-x-3">
                          <button 
                            onClick={() => handleOpenModal(subscription)}
                            className="text-blue-600 hover:text-blue-900"
                          >
                            <PencilIcon size={16} />
                            <span className="sr-only">Edit</span>
                          </button>
                          
                          <button 
                            onClick={() => subscription.id && handleDelete(subscription.id)}
                            className="text-red-600 hover:text-red-900"
                          >
                            <TrashIcon size={16} />
                            <span className="sr-only">Delete</span>
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Add/Edit Subscription Modal */}
      {showModal && (
        <div className="fixed inset-0 overflow-y-auto z-50 flex items-center justify-center">
          <div className="fixed inset-0 bg-black bg-opacity-50 transition-opacity"></div>
          
          <div className="relative bg-white rounded-lg max-w-2xl w-full mx-4 shadow-xl transform transition-all">
            <div className="px-6 py-4 border-b border-gray-200">
              <div className="flex items-center justify-between">
                <h3 className="text-lg font-medium text-gray-900">
                  {currentSubscription?.id ? 'Edit Subscription' : 'New Subscription'}
                </h3>
                <button 
                  onClick={handleCloseModal}
                  className="text-gray-400 hover:text-gray-500"
                >
                  <XCircleIcon size={20} />
                </button>
              </div>
            </div>
            
            <form onSubmit={handleSubmit}>
              <div className="px-6 py-4 space-y-4">
                <div>
                  <label htmlFor="customer_id" className="block text-sm font-medium text-gray-700 mb-1">
                    Customer *
                  </label>
                  <select
                    id="customer_id"
                    value={currentSubscription?.customer_id || ''}
                    onChange={(e) => {
                      // Ensure the value is treated as a string
                      const customerId = String(e.target.value);
                      console.log('Selected customer ID:', customerId, typeof customerId);
                      setCurrentSubscription({...currentSubscription, customer_id: customerId});
                    }}
                    required
                    className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                  >
                    <option value="">Select a customer</option>
                    {customers.map((customer) => (
                      <option key={customer.id} value={String(customer.id)}>
                        {customer.name}
                      </option>
                    ))}
                  </select>
                </div>
                
                <div>
                  <label htmlFor="plan_id" className="block text-sm font-medium text-gray-700 mb-1">
                    Plan *
                  </label>
                  <select
                    id="plan_id"
                    value={currentSubscription?.plan_id || ''}
                    onChange={(e) => {
                      // Ensure the value is treated as a string
                      const planId = String(e.target.value);
                      console.log('Selected plan ID:', planId, typeof planId);
                      setCurrentSubscription({...currentSubscription, plan_id: planId});
                    }}
                    required
                    className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                  >
                    <option value="">Select a plan</option>
                    {plans.map((plan) => (
                      <option key={plan.id} value={String(plan.id)}>
                        {plan.name} - ${plan.price} per {plan.billing_period}
                      </option>
                    ))}
                  </select>
                </div>
                
                <div>
                  <label htmlFor="status" className="block text-sm font-medium text-gray-700 mb-1">
                    Status *
                  </label>
                  <select
                    id="status"
                    value={currentSubscription?.status || ''}
                    onChange={(e) => setCurrentSubscription({...currentSubscription, status: e.target.value})}
                    required
                    className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                  >
                    <option value="active">Active</option>
                    <option value="trialing">Trialing</option>
                    <option value="canceled">Canceled</option>
                    <option value="past_due">Past Due</option>
                  </select>
                </div>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label htmlFor="start_date" className="block text-sm font-medium text-gray-700 mb-1">
                      Start Date *
                    </label>
                    <input
                      type="date"
                      id="start_date"
                      value={currentSubscription?.start_date?.split('T')[0] || ''}
                      onChange={(e) => setCurrentSubscription({...currentSubscription, start_date: e.target.value})}
                      required
                      className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                    />
                  </div>
                  
                  <div>
                    <label htmlFor="end_date" className="block text-sm font-medium text-gray-700 mb-1">
                      End Date
                    </label>
                    <input
                      type="date"
                      id="end_date"
                      value={currentSubscription?.end_date?.split('T')[0] || ''}
                      onChange={(e) => setCurrentSubscription({...currentSubscription, end_date: e.target.value || undefined})}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                    />
                  </div>
                </div>
                
                {currentSubscription?.status === 'trialing' && (
                  <div>
                    <label htmlFor="trial_end_date" className="block text-sm font-medium text-gray-700 mb-1">
                      Trial End Date
                    </label>
                    <input
                      type="date"
                      id="trial_end_date"
                      value={currentSubscription?.trial_end_date?.split('T')[0] || ''}
                      onChange={(e) => setCurrentSubscription({...currentSubscription, trial_end_date: e.target.value || undefined})}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                    />
                  </div>
                )}
                
                {currentSubscription?.status === 'active' && (
                  <div className="flex items-center">
                    <input
                      type="checkbox"
                      id="cancel_at_period_end"
                      checked={currentSubscription?.cancel_at_period_end || false}
                      onChange={(e) => setCurrentSubscription({...currentSubscription, cancel_at_period_end: e.target.checked})}
                      className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                    />
                    <label htmlFor="cancel_at_period_end" className="ml-2 text-sm text-gray-700">
                      Cancel at period end
                    </label>
                  </div>
                )}
              </div>
              
              <div className="px-6 py-4 border-t border-gray-200 flex justify-end space-x-3">
                <button
                  type="button"
                  onClick={handleCloseModal}
                  className="px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                >
                  Cancel
                </button>
                
                <button
                  type="submit"
                  className="px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                >
                  {currentSubscription?.id ? 'Update Subscription' : 'Create Subscription'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default Subscriptions;